import React from 'react';
import './CookiePolicy.css';

/**
 * Cookie Policy Component
 * 
 * Displays the cookie policy for StoicCredit application.
 * Details all cookies used, their purposes, and user rights regarding cookies.
 */
const CookiePolicy = () => {
  return (
    <div className="cookie-policy-container">
      <div className="cookie-policy-content">
        <h1>Cookie Policy</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        
        <section className="cookie-section">
          <h2>1. Introduction</h2>
          <p>
            This Cookie Policy explains how StoicCredit ("we," "our," or "us") uses cookies and similar tracking technologies when you visit our website and use our services. This policy should be read in conjunction with our Privacy Policy and Terms and Conditions.
          </p>
          <p>
            By using our Service, you consent to the use of cookies in accordance with this Cookie Policy, unless you have disabled cookies in your browser settings or through our cookie consent banner.
          </p>
        </section>

        <section className="cookie-section">
          <h2>2. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) when you visit a website. Cookies are widely used to make websites work more efficiently and to provide information to website owners.
          </p>
          <p>
            Cookies allow a website to recognize your device and store some information about your preferences or past actions. This helps us provide you with a better experience when you browse our website and also allows us to improve our services.
          </p>
        </section>

        <section className="cookie-section">
          <h2>3. Types of Cookies We Use</h2>
          
          <h3>3.1 Essential Cookies</h3>
          <p>
            <strong>Purpose:</strong> These cookies are strictly necessary for the Service to function properly. They enable core functionality such as security, network management, and accessibility.
          </p>
          <p>
            <strong>Legal Basis:</strong> These cookies are essential for the Service to work and cannot be disabled. They do not require your consent under GDPR.
          </p>
          <p>
            <strong>Examples:</strong>
          </p>
          <ul>
            <li><strong>Authentication Cookies:</strong> Used to keep you logged in and maintain your session</li>
            <li><strong>Security Cookies:</strong> Used to prevent fraud and protect your account</li>
            <li><strong>Load Balancing Cookies:</strong> Used to distribute traffic across servers</li>
          </ul>
          <p>
            <strong>Duration:</strong> Session cookies (deleted when you close your browser) and persistent cookies (remain until expiration or deletion)
          </p>

          <h3>3.2 Analytics Cookies</h3>
          <p>
            <strong>Purpose:</strong> These cookies help us understand how visitors interact with our Service by collecting and reporting information anonymously.
          </p>
          <p>
            <strong>Legal Basis:</strong> Your consent (you can opt out at any time)
          </p>
          <p>
            <strong>What We Use:</strong>
          </p>
          <ul>
            <li><strong>Firebase Analytics:</strong> We use Google Firebase Analytics to track:
              <ul>
                <li>Page views and navigation patterns</li>
                <li>Feature usage and interactions</li>
                <li>Button clicks and user actions</li>
                <li>Time spent on pages</li>
                <li>Device and browser information</li>
              </ul>
            </li>
          </ul>
          <p>
            <strong>Firebase Analytics Cookies:</strong>
          </p>
          <ul>
            <li><strong>_ga:</strong> Used to distinguish users (expires: 2 years)</li>
            <li><strong>_ga_[property-id]:</strong> Used to persist session state (expires: 2 years)</li>
            <li><strong>_gid:</strong> Used to distinguish users (expires: 24 hours)</li>
            <li><strong>_gat:</strong> Used to throttle request rate (expires: 1 minute)</li>
          </ul>
          <p>
            <strong>Data Sharing:</strong> Analytics data is shared with Google as part of Firebase Analytics. For more information, see <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.
          </p>

          <h3>3.3 Functional Cookies</h3>
          <p>
            <strong>Purpose:</strong> These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings.
          </p>
          <p>
            <strong>Legal Basis:</strong> Your consent (you can opt out at any time)
          </p>
          <p>
            <strong>Examples:</strong>
          </p>
          <ul>
            <li><strong>Preference Cookies:</strong> Remember your language, region, or display preferences</li>
            <li><strong>Cookie Consent:</strong> Remember your cookie preferences</li>
            <li><strong>User Interface Preferences:</strong> Remember your UI customizations</li>
          </ul>

          <h3>3.4 Payment Processing Cookies</h3>
          <p>
            <strong>Purpose:</strong> These cookies are used by our payment processor (Stripe) to process payments securely and prevent fraud.
          </p>
          <p>
            <strong>Legal Basis:</strong> Essential for payment processing (cannot be disabled)
          </p>
          <p>
            <strong>What We Use:</strong>
          </p>
          <ul>
            <li><strong>Stripe Cookies:</strong> Used for:
              <ul>
                <li>Payment processing and checkout</li>
                <li>Fraud prevention</li>
                <li>Security and authentication</li>
                <li>Session management during payment flows</li>
              </ul>
            </li>
          </ul>
          <p>
            <strong>Data Sharing:</strong> Payment data is processed by Stripe. For more information, see <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
          </p>
        </section>

        <section className="cookie-section">
          <h2>4. Third-Party Cookies</h2>
          <p>
            In addition to our own cookies, we may also use various third-party cookies to report usage statistics and deliver advertisements. These third parties may set their own cookies or similar technologies on your device.
          </p>
          
          <h3>4.1 Google Firebase</h3>
          <p>
            We use Google Firebase for authentication, data storage, and analytics. Firebase may set cookies on your device. For more information, see <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a>.
          </p>

          <h3>4.2 Stripe</h3>
          <p>
            We use Stripe for payment processing. Stripe may set cookies on your device during payment transactions. For more information, see <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
          </p>
        </section>

        <section className="cookie-section">
          <h2>5. Cookie Duration</h2>
          
          <h3>5.1 Session Cookies</h3>
          <p>
            Session cookies are temporary cookies that are deleted when you close your browser. They are used to maintain your session while you navigate our Service.
          </p>

          <h3>5.2 Persistent Cookies</h3>
          <p>
            Persistent cookies remain on your device for a set period or until you delete them. They are used to remember your preferences and settings across sessions.
          </p>
          <p>
            <strong>Typical Durations:</strong>
          </p>
          <ul>
            <li>Authentication cookies: Until you log out or session expires</li>
            <li>Analytics cookies: Up to 2 years</li>
            <li>Preference cookies: Up to 1 year</li>
            <li>Cookie consent: Up to 1 year</li>
          </ul>
        </section>

        <section className="cookie-section">
          <h2>6. Your Cookie Choices and Controls</h2>
          
          <h3>6.1 Cookie Consent Banner</h3>
          <p>
            When you first visit our Service, you will see a cookie consent banner that allows you to:
          </p>
          <ul>
            <li>Accept all cookies</li>
            <li>Reject non-essential cookies</li>
            <li>Customize your cookie preferences</li>
          </ul>
          <p>
            Your preferences are saved and will be remembered for future visits.
          </p>

          <h3>6.2 Browser Settings</h3>
          <p>
            You can control cookies through your browser settings. Most browsers allow you to:
          </p>
          <ul>
            <li>See what cookies you have and delete them individually</li>
            <li>Block third-party cookies</li>
            <li>Block all cookies from specific sites</li>
            <li>Block all cookies</li>
            <li>Delete all cookies when you close your browser</li>
          </ul>
          <p>
            <strong>Important:</strong> If you block or delete cookies, some features of our Service may not function properly. Essential cookies are required for the Service to work.
          </p>

          <h3>6.3 Browser-Specific Instructions</h3>
          <p>
            To manage cookies in your browser, follow these links:
          </p>
          <ul>
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
            <li><a href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
            <li><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
          </ul>

          <h3>6.4 Opt-Out of Analytics</h3>
          <p>
            You can opt out of Firebase Analytics by:
          </p>
          <ul>
            <li>Using our cookie consent banner to reject analytics cookies</li>
            <li>Installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a></li>
            <li>Adjusting your browser settings to block analytics cookies</li>
          </ul>
          <p>
            <strong>Note:</strong> Opting out of analytics cookies will not affect the functionality of our Service, but we will not be able to collect analytics data about your usage.
          </p>
        </section>

        <section className="cookie-section">
          <h2>7. Do Not Track Signals</h2>
          <p>
            Some browsers include a "Do Not Track" (DNT) feature that signals to websites you visit that you do not want to have your online activity tracked. Currently, there is no standard for how DNT signals should be interpreted. Our Service does not currently respond to DNT browser signals or mechanisms.
          </p>
          <p>
            However, you can control tracking through our cookie consent banner and your browser settings as described above.
          </p>
        </section>

        <section className="cookie-section">
          <h2>8. Updates to This Cookie Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by:
          </p>
          <ul>
            <li>Posting the updated Cookie Policy on this page</li>
            <li>Updating the "Last Updated" date at the top of this policy</li>
            <li>Showing a notice on our Service if the changes are significant</li>
          </ul>
          <p>
            We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies.
          </p>
        </section>

        <section className="cookie-section">
          <h2>9. Contact Us</h2>
          <p>
            If you have any questions about this Cookie Policy or our use of cookies, please contact us:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href="mailto:support@stoiccredit.com">support@stoiccredit.com</a></li>
          </ul>
        </section>

        <section className="cookie-section">
          <h2>10. Additional Resources</h2>
          <p>
            For more information about cookies and how to manage them, you may visit:
          </p>
          <ul>
            <li><a href="https://www.allaboutcookies.org/" target="_blank" rel="noopener noreferrer">All About Cookies</a></li>
            <li><a href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer">Your Online Choices</a> (EU)</li>
            <li><a href="https://www.networkadvertising.org/choices/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative</a> (US)</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default CookiePolicy;

